// src/components/sections/home/Hero.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';

// Extend ImgHTMLAttributes to include fetchpriority
declare module 'react' {
  interface ImgHTMLAttributes<T> extends HTMLAttributes<T> {
    fetchpriority?: 'high' | 'low' | 'auto';
  }
}

const Hero: React.FC = () => {
  const [currentImage, setCurrentImage] = useState(0);
  
  // Array of hero images for carousel with fallback
  const heroImages = [
    '/assets/images/domov/pagehero1-image.jpg',
    '/assets/images/domov/pagehero2-image.jpg',
    '/assets/images/domov/pagehero3-image.jpg',
  ];
  
  // Fallback image if any image fails to load
  const fallbackImage = '/assets/images/fallback-hero.jpg';
  
  // Auto-rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);
  
  // Handle image load errors
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn(`Failed to load image: ${e.currentTarget.src}`);
    e.currentTarget.src = fallbackImage;
  };

  return (
    <section className="hero-section relative overflow-hidden bg-gradient-to-r from-primary-100 to-neutral py-8">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          {/* Image carousel */}
          <div className="order-2 lg:order-1 flex">
            <div
              className="relative rounded-lg overflow-hidden shadow-xl w-full h-[50vh] max-h-[500px] md:h-[55vh] md:max-h-[550px] lg:h-[70vh] lg:max-h-[600px] aspect-[4/3]"
              aria-label="Image carousel"
            >
              {heroImages.map((image, index) => (
                <div
                  key={image}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-hidden={index !== currentImage}
                >
                  <img
                    src={image}
                    alt={`Bučno olje Oljarna Krajnc ${index + 1}`}
                    className="w-full h-full object-cover"
                    width={800}
                    height={600}
                    onError={handleImageError}
                    loading={index === currentImage ? 'eager' : 'lazy'}
                    fetchpriority={index === currentImage ? 'high' : 'low'}
                  />
                </div>
              ))}
              {/* Placeholder while images load */}
              <div
                className={`absolute inset-0 bg-gray-200 transition-opacity duration-1000 ${
                  heroImages[currentImage] ? 'opacity-0' : 'opacity-100'
                }`}
              />
            </div>
          </div>
          
          {/* Text content */}
          <div className="text-center lg:text-left order-1 lg:order-2 flex flex-col justify-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-700 mb-4">
              Tradicija bučnega olja od leta 1927
            </h1>
            
            <p className="text-lg md:text-xl text-accent-700 mb-4">
              Izdelki Oljarne Krajnc, narejeni po tradicionalnih receptih naših prednikov, 
              z občutkom za kakovost in spoštovanjem do narave.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link 
                to="/izdelki" 
                className="btn-primary inline-flex items-center justify-center bg-primary-600 text-white px-6 py-3 rounded-md transition-all duration-300 hover:bg-primary-900 hover:scale-105 hover:shadow-lg"
              >
                Raziskujte naše izdelke
                <ArrowRight size={18} className="ml-2" />
              </Link>
              
              <Link 
                to="/o-nas" 
                className="btn-outline inline-flex items-center justify-center border border-primary-600 text-primary-600 px-6 py-3 rounded-md transition-all duration-300 hover:bg-primary-100 hover:text-primary-900 hover:border-primary-900"
              >
                Več o naši zgodbi
                <ChevronRight size={18} className="ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;